import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Alert,
  Badge,
  Box,
  Button,
  ButtonBase,
  Chip,
  Dialog,
  DialogContent,
  Drawer,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  SvgIcon,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import { keyframes } from '@mui/material/styles'
import AddIcon from '@mui/icons-material/Add'
import FavoriteIcon from '@mui/icons-material/Favorite'
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate'
import ForumIcon from '@mui/icons-material/Forum'
import useIsMobile from '../../../../helpers/useIsMobile'
import EPCRCard from '../../../../components/Cards/EPCRCard'
import {
  CATEGORIES,
  CONSOLES,
  DEFAULT_QUICK_DRUGS,
  INDUCTION_RATIOS,
  SCENE_TIMES,
  inductionDose,
} from './catalogue'
import {
  LOG_KEYS,
  annotateEvent,
  applyTap,
  buildTap,
  countEvents,
  getAction,
  getArrestPhase,
  getCategory,
  getConsoleGroups,
  getMergedTimeline,
  getQuickDrugs,
  getToggleStates,
  isActionAvailable,
  lastEvent,
  listPendingDetails,
  retimeEvent,
  voidEvent,
} from './engine'
import {
  actionsWithClocks,
  buildArrestStart,
  fmtTime,
  timeAgo,
  toneOf,
} from './liveUi'
import { liveIcon } from './liveIcons'
import useLiveRecord from './useLiveRecord'
import CategoryGrid from './CategoryGrid'
import ConsoleStatus, { useNow } from './ConsoleStatus'
import PickerDialog from './PickerDialog'
import TapPrompt from './TapPrompt'
import LiveTimeline, { RetimeDialog } from './LiveTimeline'
import LiveDetailForm from './LiveDetailForm'
import PendingDetailsList, { dismissPending } from './PendingDetailsList'
import DebriefDialog from './DebriefDialog'

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(198, 40, 40, 0.6); }
  70% { box-shadow: 0 0 0 12px rgba(198, 40, 40, 0); }
  100% { box-shadow: 0 0 0 0 rgba(198, 40, 40, 0); }
`

const WEIGHT_SOURCES = ['Actual', 'Estimated', 'Length-based', 'Age formula']
const COUNTED_TYPES = { shock: ['shock', 'dsed', 'vectorChange', 'Shock'] }

// A category tab is either 'arrest' or a category key
const viewForCategory = (key) =>
  key === 'cardiacArrest' ? 'arrest' : key || null

// ---------------------------------------------------------------------------
// Tiles
// ---------------------------------------------------------------------------

// Compression metronome (action.metronome.bpm): counts 1-2-3-4 while
// compressions are on.
const Metronome = ({ bpm }) => {
  const [count, setCount] = useState(0)
  const period = Math.round(60000 / bpm)
  useEffect(() => {
    const id = setInterval(() => setCount((prev) => (prev + 1) % 4), period)
    return () => clearInterval(id)
  }, [period])
  return (
    <>
      <Stack direction="row" alignItems="center" gap={1.5}>
        {[0, 1, 2, 3].map((index) => (
          <Typography
            key={index}
            fontWeight={900}
            fontSize="1.4rem"
            color="inherit"
            sx={{
              opacity: index === count ? 1 : 0.45,
              transform: index === count ? 'scale(1.25)' : 'none',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {index + 1}
          </Typography>
        ))}
        <Typography
          variant="caption"
          fontWeight={800}
          color="inherit"
          sx={{ ml: 'auto', opacity: 0.85 }}
        >
          {bpm}/min
        </Typography>
      </Stack>
    </>
  )
}

const ActionTile = memo(function ActionTile({
  action,
  toggle,
  count,
  lastTime,
  now,
  onTap,
}) {
  const tone = toneOf(action.tone)
  const isOn = action.kind === 'toggle' && toggle?.on
  const Icon = liveIcon(action.icon)
  const status =
    action.kind === 'toggle'
      ? toggle
        ? `${isOn ? action.onLabel || 'On' : action.offLabel || 'Off'} · ${timeAgo(toggle.since, now)}`
        : 'Off'
      : lastTime
        ? `Last ${fmtTime(lastTime).slice(0, 5)} · ${timeAgo(lastTime, now)}`
        : action.kind === 'menu' || action.kind === 'console'
          ? action.kind === 'menu'
            ? 'Options…'
            : 'Open console'
          : ''
  return (
    <ButtonBase
      onClick={() => onTap(action)}
      sx={{
        gridColumn: action.big ? 'span 2' : 'span 1',
        minHeight: 96,
        borderRadius: 2,
        p: 1.25,
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        textAlign: 'left',
        position: 'relative',
        overflow: 'hidden',
        bgcolor: isOn ? tone.main : tone.bg,
        color: isOn ? '#fff' : tone.dark,
        border: 2,
        borderColor: tone.main,
        boxShadow: isOn ? 4 : 1,
        transition: 'transform 80ms ease',
        '&:active': { transform: 'scale(0.97)' },
      }}
    >
      <Stack width="100%" justifyContent="space-between" gap={0.5}>
        <Stack direction="row" alignItems="flex-start" gap={1}>
          {Icon && <SvgIcon component={Icon} sx={{ fontSize: 26, mt: 0.25 }} />}
          <Typography
            flex={1}
            color="inherit"
            fontWeight={800}
            fontSize={{ xs: '1.05rem', md: action.big ? '1.4rem' : '1.15rem' }}
            lineHeight={1.15}
          >
            {action.label}
          </Typography>
          {count > 0 && (
            <Box
              sx={{
                px: 1,
                borderRadius: 2,
                bgcolor: isOn ? 'rgba(255,255,255,0.25)' : tone.main,
                color: '#fff',
                fontWeight: 800,
                fontSize: '1.05rem',
              }}
            >
              ×{count}
            </Box>
          )}
        </Stack>
        {status && (
          <Typography
            variant="caption"
            fontWeight={600}
            color="inherit"
            sx={{ opacity: 0.9 }}
          >
            {status}
          </Typography>
        )}
        {isOn && action.metronome?.bpm && (
          <Metronome bpm={action.metronome.bpm} />
        )}
      </Stack>
    </ButtonBase>
  )
})

const ActionGrid = memo(function ActionGrid({ groups, events, onTap }) {
  // Coarse tick for the "x m ago" captions - the 1s tick stays in ConsoleStatus
  const now = useNow(15000)
  const toggles = useMemo(() => getToggleStates(events), [events])
  return (
    <Stack gap={2}>
      {groups.map((group) => (
        <Stack key={group.title} gap={0.75}>
          <Typography
            variant="overline"
            fontWeight={800}
            color="text.secondary"
            lineHeight={1.5}
          >
            {group.title}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, minmax(0, 1fr))',
                sm: 'repeat(3, minmax(0, 1fr))',
                xl: 'repeat(4, minmax(0, 1fr))',
              },
              gap: 1,
            }}
          >
            {group.actions.map((action) => {
              const types = COUNTED_TYPES[action.key] || [action.key]
              const count = action.counter
                ? types.reduce(
                    (total, type) => total + countEvents(events, type),
                    0,
                  )
                : 0
              return (
                <ActionTile
                  key={action.key}
                  action={action}
                  toggle={toggles[action.key]}
                  count={count}
                  lastTime={
                    action.kind === 'toggle'
                      ? null
                      : lastEvent(events, action.key)?.time
                  }
                  now={now}
                  onTap={onTap}
                />
              )
            })}
          </Box>
        </Stack>
      ))}
    </Stack>
  )
})

// ---------------------------------------------------------------------------
// Console
// ---------------------------------------------------------------------------

// selectedEPCRModule 'Live' (critical care console, landing grid first) or
// 'Arrest' (arrest console). Non-critical-care records only ever get the
// arrest console, at their own level.
const LiveModule = ({ initialView = 'Live' }) => {
  const {
    formValues,
    updateRecord,
    handleChange,
    user,
    orgSettings,
    setSelectedEPCRModule,
  } = useLiveRecord()
  const isMobile = useIsMobile()
  const isCC = formValues?.mode === 'Critical Care'
  const recordLevel = isCC ? 'Critical Care' : formValues?.mode || 'ePCR'
  const cardiacArrest = formValues?.cardiacArrest
  const criticalCare = formValues?.criticalCare
  const categories = criticalCare?.categories ?? []
  const arrestEvents = cardiacArrest?.events
  const ccEvents = criticalCare?.events
  const arrestStarted = !!cardiacArrest?.startedAt
  const cycleLengthSeconds =
    cardiacArrest?.cycleLengthSeconds ||
    orgSettings?.cardiacArrestProtocol?.cprCycleLengthSeconds ||
    120

  const [view, setView] = useState(() =>
    initialView === 'Arrest' || !isCC
      ? 'arrest'
      : viewForCategory(categories[0]),
  )
  const [picker, setPicker] = useState(null)
  const [prompt, setPrompt] = useState(null)
  const [detailRequest, setDetailRequest] = useState(null)
  const [pendingOpen, setPendingOpen] = useState(false)
  const [debriefOpen, setDebriefOpen] = useState(false)
  const [addingCategory, setAddingCategory] = useState(false)
  const [retimeScene, setRetimeScene] = useState(null)

  // Latest record for building taps between renders
  const recordRef = useRef(formValues)
  recordRef.current = formValues

  useEffect(() => {
    if (initialView === 'Arrest' || !isCC) setView('arrest')
  }, [initialView, isCC])

  // Categories picked on another device / from the grid
  useEffect(() => {
    if (!view && categories.length) setView(viewForCategory(categories[0]))
  }, [view, categories])

  const isArrestView = view === 'arrest'
  const phase = useMemo(
    () => getArrestPhase(arrestEvents || []),
    [arrestEvents],
  )
  // A record switched to Critical Care after the arrest started still gets
  // the CC actions (matches mobile, which reads the current mode)
  const arrestLevel =
    recordLevel === 'Critical Care'
      ? 'Critical Care'
      : cardiacArrest?.level || recordLevel
  const consoleKey = isArrestView
    ? 'arrest'
    : getCategory(view)?.console || view
  const consoleLevel = isArrestView ? arrestLevel : 'Critical Care'
  const groups = useMemo(
    () => getConsoleGroups(consoleKey, { phase, level: consoleLevel }),
    [consoleKey, phase, consoleLevel],
  )
  const currentEvents = useMemo(
    () => (isArrestView ? arrestEvents : ccEvents) || [],
    [isArrestView, arrestEvents, ccEvents],
  )
  const mergedEvents = useMemo(
    () => getMergedTimeline({ cardiacArrest, criticalCare }),
    [cardiacArrest, criticalCare],
  )
  const clockKeys = useMemo(() => {
    const onConsole = new Set()
    groups.forEach((group) =>
      group.actions.forEach((action) => {
        onConsole.add(action.key)
        ;(action.items || []).forEach((key) => onConsole.add(key))
      }),
    )
    // Running clocks (tourniquet, REBOA, Code Red…) follow you across consoles
    return actionsWithClocks.filter((key) => {
      const mode = getAction(key).clock.mode
      return onConsole.has(key) || mode === 'whileOn' || mode === 'sinceFirst'
    })
  }, [groups])
  const statusRecord = useMemo(
    () => ({ cardiacArrest, criticalCare }),
    [cardiacArrest, criticalCare],
  )
  const pending = useMemo(
    () => listPendingDetails(formValues),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      formValues?.treatment,
      formValues?.assess,
      formValues?.vitals,
      cardiacArrest,
      criticalCare,
    ],
  )

  // ---- record writes -------------------------------------------------------

  const startArrest = useCallback(() => {
    if (!recordRef.current?.cardiacArrest?.startedAt) {
      updateRecord(
        buildArrestStart({
          level: recordLevel,
          cycleLengthSeconds:
            orgSettings?.cardiacArrestProtocol?.cprCycleLengthSeconds || 120,
          userId: user?._id,
        }),
      )
    }
    setView('arrest')
  }, [updateRecord, recordLevel, orgSettings, user])

  const addCategories = useCallback(
    (keys) => {
      if (!keys.length) return
      const now = new Date().toISOString()
      updateRecord((record) => {
        const existing = record.criticalCare?.categories ?? []
        return {
          ...record,
          criticalCare: {
            ...(record.criticalCare || {}),
            startedAt: record.criticalCare?.startedAt || now,
            categories: [
              ...existing,
              ...keys.filter((key) => !existing.includes(key)),
            ],
            events: record.criticalCare?.events || [],
          },
        }
      })
      if (keys[0] === 'cardiacArrest') startArrest()
      else setView(keys[0])
    },
    [updateRecord, startArrest],
  )

  const doTap = useCallback(
    (action, { choice, drug, on, logKey: forcedLogKey } = {}) => {
      const record = recordRef.current
      const logKey =
        forcedLogKey || (isArrestView ? LOG_KEYS.arrest : LOG_KEYS.criticalCare)
      const onArrestLog = logKey === LOG_KEYS.arrest
      const tap = buildTap({
        actionKey: action.key,
        choice,
        drug,
        on,
        phase: onArrestLog
          ? getArrestPhase(record?.cardiacArrest?.events || [])
          : undefined,
        category: onArrestLog
          ? 'cardiacArrest'
          : isArrestView
            ? undefined
            : view,
        userId: user?._id,
        // Number attempts / units across both logs (matches mobile): an
        // intubation on the CC console then one on the arrest console = 1, 2
        log: getMergedTimeline(record || {}),
        weightKg: record?.criticalCare?.weightKg,
      })
      const startedAt = tap.event.time
      const defaults = onArrestLog
        ? { startedAt, cycleLengthSeconds, level: recordLevel }
        : { startedAt, categories: [] }
      updateRecord((current) => applyTap(current, logKey, tap, defaults))

      const request = action.detailForm
        ? {
            section: tap.section || action.target?.section,
            entryId: tap.event.entryId,
            eventId: tap.event.id,
            formKey: action.detailForm,
          }
        : null
      setPrompt({ event: tap.event, logKey, action, request })
      if (action.prompt === 'form' && request) setDetailRequest(request)
    },
    [isArrestView, view, user, cycleLengthSeconds, recordLevel, updateRecord],
  )

  const level = consoleLevel

  const handleAction = useCallback(
    (action) => {
      if (!action) return
      switch (action.kind) {
        case 'console': {
          const category = CATEGORIES.find(
            (item) => item.console === action.opens,
          )
          if (!category) return
          if (
            !(recordRef.current?.criticalCare?.categories ?? []).includes(
              category.key,
            )
          ) {
            addCategories([category.key])
          } else {
            setView(viewForCategory(category.key))
          }
          return
        }
        case 'menu':
          setPicker({
            action,
            type: 'menu',
            options: (action.items || [])
              .map(getAction)
              .filter((item) => isActionAvailable(item, level))
              .map((item) => ({
                key: item.key,
                label: item.label,
                tone: item.tone,
                sublabel: item.kind === 'toggle' ? 'On / off' : undefined,
              })),
          })
          return
        case 'choice': {
          const weightKg = recordRef.current?.criticalCare?.weightKg
          setPicker({
            action,
            type: 'choice',
            options: (action.choices || []).map((choice) => {
              const value = typeof choice === 'string' ? choice : choice.value
              let sublabel
              if (action.inductionDrugs?.length) {
                const ratio = INDUCTION_RATIOS.find(
                  (item) => item.value === value,
                )
                sublabel =
                  weightKg && ratio?.perKg
                    ? action.inductionDrugs
                        .map((name) => {
                          const dose = inductionDose(name, value, weightKg)
                          return dose ? `${name} ${dose}` : null
                        })
                        .filter(Boolean)
                        .join(' · ')
                    : ratio?.perKg
                      ? 'Record a weight to pre-fill doses'
                      : undefined
              }
              return {
                key: value,
                label: typeof choice === 'string' ? choice : choice.label,
                value: choice,
                sublabel,
              }
            }),
          })
          return
        }
        case 'drug': {
          const drugs = getQuickDrugs({
            category: isArrestView ? 'cardiacArrest' : view,
            tenantDrugs: orgSettings?.criticalCareSettings?.quickDrugs,
            defaults: DEFAULT_QUICK_DRUGS,
            arrestProtocolDrugs: isArrestView
              ? orgSettings?.cardiacArrestProtocol?.drugs
              : undefined,
          })
          setPicker({
            action,
            type: 'drug',
            options: drugs.map((drug, index) => ({
              key: `${drug.name}|${drug.dose}|${index}`,
              label: drug.name,
              sublabel: [drug.dose, drug.route].filter(Boolean).join(' '),
              value: { name: drug.name, dose: drug.dose, route: drug.route },
              tone: 'green',
            })),
          })
          return
        }
        case 'toggle': {
          const events =
            (isArrestView
              ? recordRef.current?.cardiacArrest?.events
              : recordRef.current?.criticalCare?.events) || []
          doTap(action, { on: !getToggleStates(events)[action.key]?.on })
          return
        }
        default:
          doTap(action)
      }
    },
    [level, isArrestView, view, orgSettings, doTap, addCategories],
  )

  const pickOption = (option) => {
    const current = picker
    setPicker(null)
    if (!current) return
    if (current.type === 'menu') handleAction(getAction(option.key))
    else if (current.type === 'choice')
      doTap(current.action, { choice: option.value })
    else if (current.type === 'drug')
      doTap(current.action, { drug: option.value })
  }

  const closePrompt = useCallback(() => setPrompt(null), [])

  // ---- rendering -----------------------------------------------------------

  if (isCC && !categories.length && initialView !== 'Arrest') {
    return (
      <EPCRCard>
        <CategoryGrid onConfirm={addCategories} />
      </EPCRCard>
    )
  }

  const phaseBanner = isArrestView
    ? CONSOLES.arrest.phases[phase]?.banner
    : null
  const reminders = !isArrestView ? CONSOLES[consoleKey]?.reminders : null
  const tabs = [
    ...(arrestStarted || isArrestView ? ['arrest'] : []),
    ...categories.filter((key) => key !== 'cardiacArrest'),
  ]

  const sceneTimes = isCC
    ? SCENE_TIMES.map((key) => ({
        action: getAction(key),
        event: lastEvent(ccEvents, key),
      }))
    : []

  return (
    <Stack gap={1.5} sx={{ pb: prompt ? 18 : 2 }}>
      {/* Header: categories, ARREST */}
      <Paper sx={{ p: 1, borderRadius: 2 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          {isCC ? (
            <Tabs
              value={tabs.includes(view) ? view : false}
              onChange={(e, next) => setView(next)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                flex: 1,
                minWidth: 0,
                '& .MuiTab-root': { minHeight: 56, fontWeight: 800 },
              }}
            >
              {tabs.map((key) => (
                <Tab
                  key={key}
                  value={key}
                  label={
                    key === 'arrest'
                      ? 'Cardiac Arrest'
                      : getCategory(key)?.label || key
                  }
                  sx={{
                    color: toneOf(
                      key === 'arrest' ? 'red' : getCategory(key)?.tone,
                    ).main,
                  }}
                />
              ))}
            </Tabs>
          ) : (
            <Typography
              variant="h6"
              fontWeight={800}
              flex={1}
              color="error.main"
            >
              Cardiac arrest
            </Typography>
          )}
          {isCC && (
            <Button
              onClick={() => setAddingCategory(true)}
              startIcon={<AddIcon />}
              sx={{ minHeight: 48, whiteSpace: 'nowrap' }}
            >
              {isMobile ? 'Add' : 'Add category'}
            </Button>
          )}
          {isCC && !isArrestView && (
            <Button
              variant="contained"
              color="error"
              onClick={startArrest}
              startIcon={<FavoriteIcon />}
              sx={{
                minHeight: 56,
                px: { xs: 1.5, md: 3 },
                fontSize: { xs: '1rem', md: '1.2rem' },
                fontWeight: 900,
                animation:
                  arrestStarted && phase === 'arrest'
                    ? `${pulse} 1.5s infinite`
                    : undefined,
              }}
            >
              Arrest
            </Button>
          )}
        </Stack>
      </Paper>

      {/* Scene times + weight (critical care) */}
      {isCC && (
        <Paper sx={{ p: 1, borderRadius: 2 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            gap={1}
            alignItems={{ md: 'center' }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                gap: 0.75,
                flex: 1,
              }}
            >
              {sceneTimes.map(({ action, event }) => (
                <ButtonBase
                  key={action.key}
                  onClick={() =>
                    event
                      ? setRetimeScene(event)
                      : doTap(action, { logKey: LOG_KEYS.criticalCare })
                  }
                  sx={{
                    minHeight: 56,
                    borderRadius: 2,
                    border: 2,
                    borderColor: event ? 'success.main' : 'divider',
                    bgcolor: event ? 'success.light' : 'grey.100',
                    color: event ? 'success.contrastText' : 'text.primary',
                    flexDirection: 'column',
                    px: 0.5,
                  }}
                >
                  <Typography
                    fontWeight={800}
                    fontSize="0.9rem"
                    lineHeight={1.1}
                    textAlign="center"
                  >
                    {action.label}
                  </Typography>
                  <Typography fontFamily="monospace" fontSize="0.85rem">
                    {event ? fmtTime(event.time).slice(0, 5) : 'Tap'}
                  </Typography>
                </ButtonBase>
              ))}
            </Box>
            <Stack direction="row" gap={1}>
              <TextField
                label="Weight"
                type="number"
                size="small"
                value={criticalCare?.weightKg ?? ''}
                onChange={(e) =>
                  handleChange({
                    moduleName: 'criticalCare',
                    field: 'weightKg',
                    value:
                      e.target.value === ''
                        ? undefined
                        : Number(e.target.value),
                  })
                }
                inputProps={{ inputMode: 'decimal', min: 0 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">kg</InputAdornment>
                  ),
                }}
                sx={{ width: 120 }}
              />
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Source</InputLabel>
                <Select
                  label="Source"
                  value={criticalCare?.weightSource ?? ''}
                  onChange={(e) =>
                    handleChange({
                      moduleName: 'criticalCare',
                      field: 'weightSource',
                      value: e.target.value,
                    })
                  }
                >
                  {WEIGHT_SOURCES.map((source) => (
                    <MenuItem key={source} value={source}>
                      {source}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Stack>
        </Paper>
      )}

      {isArrestView && !arrestStarted ? (
        <EPCRCard>
          <Stack gap={2} alignItems="center" textAlign="center" py={2}>
            <Typography variant="h5" fontWeight={800}>
              Cardiac arrest console
            </Typography>
            <Typography color="text.secondary" maxWidth={560}>
              Starting the console logs CPR on now and starts the arrest and
              {` ${Math.round(cycleLengthSeconds / 60)}`}-minute cycle timers.
              Correct the start time from the timeline if the arrest began
              earlier.
            </Typography>
            <Button
              variant="contained"
              color="error"
              size="large"
              onClick={startArrest}
              startIcon={<FavoriteIcon />}
              sx={{ minHeight: 72, px: 5, fontSize: '1.4rem', fontWeight: 900 }}
            >
              Start arrest - CPR on
            </Button>
          </Stack>
        </EPCRCard>
      ) : (
        <>
          {phaseBanner && (
            <Alert
              severity={phase === 'rosc' ? 'success' : 'info'}
              variant="filled"
              sx={{ fontSize: '1.1rem', fontWeight: 800 }}
            >
              {phaseBanner}
            </Alert>
          )}
          <ConsoleStatus
            record={statusRecord}
            mergedEvents={mergedEvents}
            clockKeys={clockKeys}
            showArrest={isArrestView}
            phase={phase}
            level={arrestLevel}
            cycleLengthSeconds={cycleLengthSeconds}
            onRhythmCheck={() => handleAction(getAction('rhythmCheck'))}
          />
          {reminders?.length > 0 && (
            <Stack direction="row" gap={1} flexWrap="wrap">
              {reminders.map((reminder) => (
                <Chip
                  key={reminder}
                  label={reminder}
                  color="secondary"
                  variant="outlined"
                />
              ))}
            </Stack>
          )}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                lg: 'minmax(0, 2fr) minmax(280px, 1fr)',
              },
              gap: 1.5,
              alignItems: 'start',
            }}
          >
            <ActionGrid
              groups={groups}
              events={currentEvents}
              onTap={handleAction}
            />
            <Stack
              gap={1.5}
              sx={{ position: { lg: 'sticky' }, top: { lg: 8 } }}
            >
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Badge
                  badgeContent={pending.length}
                  color="warning"
                  sx={{ flex: 1 }}
                >
                  <Button
                    fullWidth
                    variant="outlined"
                    color="warning"
                    startIcon={<AssignmentLateIcon />}
                    onClick={() => setPendingOpen(true)}
                    sx={{ minHeight: 48, fontWeight: 700 }}
                  >
                    To complete ({pending.length})
                  </Button>
                </Badge>
                {isCC && (
                  <Button
                    variant="outlined"
                    startIcon={<ForumIcon />}
                    onClick={() => setDebriefOpen(true)}
                    sx={{ minHeight: 48, fontWeight: 700, flex: 1 }}
                  >
                    {criticalCare?.debrief?.completedAt
                      ? 'Debrief ✓'
                      : 'End / Debrief'}
                  </Button>
                )}
              </Stack>
              <LiveTimeline
                onOpenDetails={setDetailRequest}
                maxHeight={isMobile ? undefined : '60vh'}
              />
              {!isCC && (
                <Button
                  onClick={() => setSelectedEPCRModule('Treatment')}
                  sx={{ minHeight: 44 }}
                >
                  Go to Treatment
                </Button>
              )}
            </Stack>
          </Box>
        </>
      )}

      <PickerDialog
        open={!!picker}
        title={picker?.action?.label}
        hint={picker?.action?.hint}
        tone={picker?.action?.tone}
        options={picker?.options}
        allowOther={picker?.type === 'drug'}
        onPick={pickOption}
        onClose={() => setPicker(null)}
      />
      <TapPrompt
        prompt={prompt}
        onClose={closePrompt}
        onUndo={() => {
          const { logKey, event } = prompt
          updateRecord((record) => voidEvent(record, logKey, event.id))
          setPrompt(null)
        }}
        onChip={(chip) => {
          const { logKey, event } = prompt
          updateRecord((record) =>
            annotateEvent(record, logKey, event.id, chip),
          )
        }}
        onAddDetails={() => {
          setDetailRequest(prompt.request)
          setPrompt(null)
        }}
      />
      <LiveDetailForm
        request={detailRequest}
        onClose={() => setDetailRequest(null)}
      />
      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        open={pendingOpen}
        onClose={() => setPendingOpen(false)}
        PaperProps={{
          sx: {
            width: { md: 440 },
            maxHeight: { xs: '85vh', md: '100%' },
            p: 2,
          },
        }}
      >
        <Stack gap={1.5}>
          <Typography variant="h6" fontWeight={800}>
            Details to complete
          </Typography>
          <PendingDetailsList
            items={pending}
            onOpen={(request) => {
              setPendingOpen(false)
              setDetailRequest(request)
            }}
            onDismiss={(item) => updateRecord(dismissPending(item))}
          />
          <Button onClick={() => setPendingOpen(false)}>Close</Button>
        </Stack>
      </Drawer>
      <DebriefDialog open={debriefOpen} onClose={() => setDebriefOpen(false)} />
      <Dialog
        open={addingCategory}
        onClose={() => setAddingCategory(false)}
        fullWidth
        maxWidth="lg"
        fullScreen={isMobile}
      >
        <DialogContent>
          <CategoryGrid
            existing={categories}
            title="Add a category"
            confirmLabel="Add"
            onCancel={() => setAddingCategory(false)}
            onConfirm={(keys) => {
              setAddingCategory(false)
              addCategories(keys)
            }}
          />
        </DialogContent>
      </Dialog>
      {retimeScene && (
        <RetimeDialog
          event={retimeScene}
          onClose={() => setRetimeScene(null)}
          onSave={(time) => {
            const id = retimeScene.id
            updateRecord((record) =>
              retimeEvent(record, LOG_KEYS.criticalCare, id, time),
            )
            setRetimeScene(null)
          }}
        />
      )}
    </Stack>
  )
}

export default LiveModule
